import { ActivatedRoute } from '@angular/router';
import {
  CobrancaDTO,
  FormaPagamento,
  FormaPagamentoNormalizado,
  Invoice,
  Locale,
} from '../core/types';
import {
  copyDetails,
  dicionarioItensContrato,
  invoiceStatus,
  tipoDocumentDic,
} from './../core/dictionary';
import { Component, OnInit } from '@angular/core';
import { PagamentoService } from '../core/services/pagamento.service';
import { take } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { Utils } from '../@shared/utils/utils';
import { logoBase64 } from '../@shared/utils/logoBase64';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { vfs } from '../assets/fonts/arial_vfs_fonts';
import { format, parse } from 'date-fns';
(pdfMake as any).vfs = vfs;
(pdfMake as any).fonts = {
  Arial: {
    normal: 'ARIAL.TTF',
    bold: 'ARIALBD.TTF',
    italics: 'ARIALI.TTF',
    bolditalics: 'ARIALBI.TTF',
  },
};
import { ptBR } from 'date-fns/locale';
import { AngularFireAnalytics } from '@angular/fire/compat/analytics';
import { ReciboCabecalho, reciboCabecalhos } from '../core/recibo-cabecalhos';

@Component({
  selector: 'app-details',
  templateUrl: './details.component.html',
  styleUrls: ['./details.component.scss'],
})
export class DetailsComponent implements OnInit {
  public invoice = {} as Invoice;
  public cobranca: CobrancaDTO | null = null;
  public formaPagamento: FormaPagamento | null = null;
  public locale: Locale = 'ptBR';
  public copy = copyDetails;
  public invoiceStatus = invoiceStatus;
  public dicionarioItensContrato = dicionarioItensContrato;
  public loading = true;
  public valid = true;
  public loadingPayment = false;
  public customProviderPayment = false;
  public paymentError: string = '';

  providerRedirect: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private pagamentoService: PagamentoService,
    private analytics: AngularFireAnalytics,
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      if (params['uuid']) {
        this.pagamentoService
          .getInvoice(params['uuid'])
          .pipe(take(1))
          .subscribe({
            next: (res) => {
              this.invoice = res;
              this.loading = false;
            },
            error: () => {
              this.valid = false;
            },
          });
      }
    });

    this.route.queryParams.subscribe((queryParams) => {
      this.providerRedirect = queryParams['redirect_status'];
    });
  }

  normalizeNomeFormaPagamento(nome: string) {
    return Utils.normalize(nome) as FormaPagamentoNormalizado;
  }

  processPayment(formaPagamento: FormaPagamento) {
    var provedor = formaPagamento.detalhes['provedor'].nome.toLocaleLowerCase();
    this.formaPagamento = formaPagamento;

    this.paymentError = '';

    if (
      (provedor == 'cielo' &&
        this.normalizeNomeFormaPagamento(formaPagamento.tipo) === 'cartao') ||
      this.normalizeNomeFormaPagamento(formaPagamento.tipo) === 'cartao_debito'
    ) {
      this.customProviderPayment = true;
    } else {
      this.loadingPayment = true;
      this.cobranca = null;

      var pagamentoFatura = {
        uuidFormaPagamento: formaPagamento.uuid,
      };

      this.analytics.logEvent('process_payment_attempt', {
        invoice_uuid: this.invoice.uuid,
        forma_pagamento_uuid: formaPagamento.uuid,
        tipo: formaPagamento.tipo,
        provider: provedor,
      });

      this.pagamentoService
        .payInvoice(this.invoice.uuid, pagamentoFatura)
        .subscribe({
          next: (cobranca) => {
            this.loadingPayment = false;
            this.paymentError = '';
            this.cobranca = cobranca;

            this.analytics.logEvent('process_payment_success', {
              invoice_uuid: this.invoice.uuid,
              forma_pagamento_uuid: formaPagamento.uuid,
              status: cobranca?.status,
              provider: provedor,
            });
          },
          error: (err) => {
            this.loadingPayment = false;
            this.paymentError = err.error.message;

            this.analytics.logEvent('process_payment_error', {
              invoice_uuid: this.invoice.uuid,
              forma_pagamento_uuid: formaPagamento.uuid,
              status: err.status,
              message: err.error?.message,
              response: JSON.stringify(err.error || {}),
              provider: provedor,
            });

            Swal.fire({
              icon: 'error',
              title: 'Erro',
              text:
                err.error?.message ||
                'Houve um problema com o pagamento!\nEntre em contato com o suporte da JFL para mais informações.',
              confirmButtonColor: '#92C5CE',
            });
          },
        });
    }
  }

  showReceipt() {
    if (this.invoice == null) {
      return false;
    }

    if (this.invoice.status === 'PAGA') {
      return true;
    }

    return false;
  }

  formatPaymentDate(dateString: string): string {
    const date = new Date(dateString);

    return format(date, "d 'de' MMM 'de' yyyy, HH:mm:ss", { locale: ptBR });
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  }

  formatCNPJ(cnpj: string): string {
    return cnpj.replace(
      /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
      '$1.$2.$3/$4-$5',
    );
  }

  formatCPF(cpf: string): string {
    return cpf.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  }

  formatCEP(cep: string): string {
    if (!cep) return '';
    const cleaned = cep.replace(/\D/g, '');
    return cleaned.replace(/^(\d{5})(\d{3})$/, '$1-$2');
  }

  formatDocument(documento: string, tipo: string): string {
    if (documento === null) {
      return '';
    }
    const cleaned = documento?.replace(/\D/g, '');

    switch (tipo.toUpperCase()) {
      case 'CPF':
        return this.formatCPF(cleaned);
      case 'CNPJ':
        return this.formatCNPJ(cleaned);
      default:
        return documento;
    }
  }

  pegarCabecalho(
    empreendimentoNome: string,
    tipologia: string,
  ): ReciboCabecalho {
    const key = empreendimentoNome.replace(/\s+/g, '').toUpperCase();

    const config = reciboCabecalhos[key];

    const isCS = /\bCS\b/i.test(
      tipologia?.replace(/[-–—]/g, ' ')?.replace(/\s+/g, ' ')?.toUpperCase() ??
        '',
    );

    if (config.spe2) {
      return isCS ? config.spe2 : config.spe1;
    }

    return config.spe1;
  }

  async generateReceipt() {
    const cabecalho = this.pegarCabecalho(
      this.invoice.empreendimentoNome,
      this.invoice.unidadeTipologia,
    );

    const formattedDate = this.formatPaymentDate(this.invoice.dataPagamento);
    const formattedTotalValue = this.formatCurrency(this.invoice.valorTotal);
    const formattedDocType =
      tipoDocumentDic[this.invoice.contrato.tipoDocumento]?.label ??
      'Documento';

    const formattedDocument = this.formatDocument(
      this.invoice.contrato.documento,
      this.invoice.contrato.tipoDocumento,
    );

    const competenciaTexto =
      this.invoice?.dataInicioCompetencia && this.invoice?.dataFimCompetencia
        ? `${format(
            this.toLocalDate(this.invoice.dataInicioCompetencia),
            'dd/MM/yyyy',
          )} - ${format(
            this.toLocalDate(this.invoice.dataFimCompetencia),
            'dd/MM/yyyy',
          )}`
        : null;

    const tituloPadrao = {
      width: 'auto',
      style: 'fieldLabel',
      margin: [0, 0, 5, 0],
    };

    const valorPadrao = {
      style: 'dataValue',
    };

    const numeroRecibo = this.invoice.nsu
      ? this.invoice.nsu
      : this.invoice.uuid.substring(
          this.invoice.uuid.length - 8,
          this.invoice.uuid.length,
        );

    const locatario = this.invoice.locatario;
    const complemento = locatario.complemento
      ? ` - ${locatario.complemento}`
      : '';
    const endereco = `${locatario.tipoLogradouro} ${locatario.logradouro}, ${
      locatario.numero
    }${complemento} - ${locatario.bairro} - CEP: ${this.formatCEP(
      locatario.cep,
    )}`;
    const unidade = `${this.invoice.unidadeNumero} - ${this.invoice.empreendimentoNome}`;

    const cabecalhoRazao = cabecalho.razaoSocial;
    const cabecalhoCorpo = cabecalho.cabecalho;

    const docDefinition: any = {
      background: (_: any, pageSize: any) => {
        const height = 80;
        const width = pageSize.width;

        return {
          canvas: [
            {
              type: 'polyline',
              closePath: true,
              color: '#8E4E38',
              lineColor: '#8E4E38',
              points: [
                { x: 0, y: 0 },
                { x: width, y: 0 },
                { x: 0, y: height },
              ],
            },
          ],
        };
      },

      content: [
        {
          table: {
            widths: ['*'],
            body: [
              [
                {
                  stack: [
                    {
                      image: logoBase64,
                      width: 100,
                      alignment: 'right',
                      margin: [0, 10, 0, 10],
                    },

                    {
                      text: 'Recibo',
                      style: 'mainTitle',
                      margin: [0, 10, 0, 10],
                    },

                    // Data de pagamento
                    {
                      alignment: 'left',
                      columns: [
                        { text: 'Data de Pagamento:', ...tituloPadrao },
                        { text: formattedDate, style: 'dataValue' },
                      ],
                    },

                    // Forma de Pagamento
                    ...(!this.invoice?.formaPagamentoUtilizada?.nome
                      ? []
                      : [
                          {
                            alignment: 'left',
                            columns: [
                              { text: 'Forma de Pagamento:', ...tituloPadrao },
                              {
                                text: this.invoice.formaPagamentoUtilizada.nome,
                                ...valorPadrao,
                              },
                            ],
                          },
                        ]),

                    // Numero Recibo
                    {
                      alignment: 'left',
                      columns: [
                        { text: 'Número do Recibo:', ...tituloPadrao },
                        { text: numeroRecibo, ...valorPadrao },
                      ],
                    },

                    // ID
                    {
                      alignment: 'left',
                      columns: [
                        { text: 'ID:', ...tituloPadrao },
                        {
                          text: this.invoice?.nsu || this.invoice?.uuid,
                          ...valorPadrao,
                        },
                      ],
                    },

                    // Cabecalho
                    {
                      alignment: 'left',
                      margin: [0, 30, 0, 0],
                      columns: [
                        {
                          text: [
                            {
                              text: cabecalhoRazao,
                              style: 'dataValue',
                              bold: true,
                              alignment: 'justify',
                            },
                            {
                              text: cabecalhoCorpo,
                              style: 'dataValue',
                              alignment: 'justify',
                            },
                          ],
                        },
                      ],
                    },

                    // Locatário
                    {
                      alignment: 'left',
                      margin: [0, 30, 0, 0],
                      columns: [
                        { text: 'Cliente:', ...tituloPadrao },
                        {
                          text: this.invoice.contrato.locatario,
                          ...valorPadrao,
                        },
                      ],
                    },

                    ...(!this.invoice.contrato?.documento
                      ? []
                      : [
                          {
                            alignment: 'left',
                            columns: [
                              { text: `${formattedDocType}:`, ...tituloPadrao },
                              { text: formattedDocument, ...valorPadrao },
                            ],
                          },
                        ]),

                    {
                      alignment: 'left',
                      columns: [
                        { text: 'End:', ...tituloPadrao, width: 'auto' },
                        { text: endereco, ...valorPadrao, width: '*' },
                      ],
                      columnGap: 5,
                    },

                    {
                      alignment: 'left',
                      columns: [
                        { text: 'Morador:', ...tituloPadrao },
                        { text: this.invoice.morador.nome, ...valorPadrao },
                      ],
                    },

                    {
                      alignment: 'left',
                      columns: [
                        { text: 'Unidade:', ...tituloPadrao },
                        { text: unidade, ...valorPadrao },
                      ],
                    },

                    // Reserva
                    {
                      margin: [0, 30, 0, 0],
                      columns: [
                        {
                          width: '*',
                          stack: [
                            { text: 'Reserva', style: 'dataDescricao' },
                            { text: this.invoice.codigo, style: 'dataValue' },
                          ],
                        },
                        ...(competenciaTexto
                          ? [
                              {
                                width: 'auto',
                                alignment: 'right',
                                stack: [
                                  {
                                    text: 'Competência',
                                    style: 'dataDescricao',
                                    alignment: 'right',
                                  },
                                  {
                                    text: competenciaTexto,
                                    style: 'dataValue',
                                    alignment: 'right',
                                  },
                                ],
                              },
                            ]
                          : []),
                      ],
                    },

                    // Linha
                    {
                      canvas: [
                        {
                          type: 'line',
                          x1: 0,
                          y1: 0,
                          x2: 455,
                          y2: 0,
                          lineWidth: 0.5,
                          lineColor: '#e5e7eb',
                        },
                      ],
                      margin: [0, 10],
                    },

                    {
                      margin: [0, 0, 0, 0],
                      columns: [
                        {
                          width: '*',
                          text: 'Descrição',
                          style: 'dataDescricao',
                        },
                        {
                          width: 'auto',
                          text: 'Valor',
                          style: 'dataDescricao',
                          alignment: 'right',
                        },
                      ],
                    },

                    {
                      table: {
                        widths: ['*'],
                        body: [
                          [
                            {
                              table: {
                                widths: ['*', 'auto'],
                                body: [
                                  [
                                    {
                                      text: 'Pagamento para JFL LIVING',
                                      style: 'itemText',
                                    },
                                    {
                                      text: formattedTotalValue,
                                      style: 'itemText',
                                      alignment: 'right',
                                    },
                                  ],
                                  [
                                    { text: 'Valor Pago', style: 'totalText' },
                                    {
                                      text: formattedTotalValue,
                                      style: 'totalText',
                                      alignment: 'right',
                                    },
                                  ],
                                ],
                              },
                              layout: {
                                hLineWidth: () => 0,
                                vLineWidth: () => 0,
                                paddingLeft: () => 0,
                                paddingRight: () => 0,
                                paddingTop: () => 4,
                                paddingBottom: () => 0,
                              },
                              margin: [0, 0, 0, 0],
                            },
                          ],
                        ],
                      },
                      layout: {
                        defaultBorder: false,
                        paddingLeft: () => 0,
                        paddingRight: () => 0,
                      },
                      margin: [0, 0, 0, 0],
                    },

                    {
                      canvas: [
                        {
                          type: 'line',
                          x1: 0,
                          y1: 5,
                          x2: 455,
                          y2: 5,
                          lineWidth: 0.5,
                          lineColor: '#e5e7eb',
                        },
                      ],
                    },
                    { text: '', margin: [0, 30] },
                    {
                      // Caixa com fundo cinza claro e padding
                      layout: 'noBorders',
                      table: {
                        widths: ['*'],
                        body: [
                          [
                            {
                              margin: [10, 10, 10, 10], // padding: left, top, right, bottom
                              fillColor: '#f9fafb',
                              stack: [
                                {
                                  text: [
                                    'Em caso de dúvidas, entre em contato pelo e-mail ',
                                    {
                                      text: 'locacao@jflliving.com.br',
                                      link: 'mailto:locacao@jflliving.com.br',
                                      style: 'link',
                                    },
                                    ' ou pelo telefone ',
                                    {
                                      text: '+55 11 97679-5897',
                                      link: 'tel:+5511976795897',
                                      style: 'link',
                                    },
                                    '.',
                                  ],
                                  style: 'footerText',
                                  alignment: 'left',
                                  margin: [0, 0, 0, 5],
                                },
                                {
                                  text: ['Estamos à disposição para ajudar.'],
                                  style: 'footerText',
                                  alignment: 'left',
                                  margin: [0, 0, 0, 0],
                                },
                              ],
                            },
                          ],
                        ],
                      },
                    },
                  ],
                  layout: {
                    hLineWidth: () => 0,
                    vLineWidth: () => 0,
                  },
                  margin: [30, 0, 30, 30],
                },
              ],
            ],
          },
          layout: {
            hLineWidth: () => 0,
            vLineWidth: () => 0,
          },
        },
      ],
      styles: {
        mainTitle: {
          fontSize: 20,
          bold: true,
          color: '#32325d',
          alignment: 'left',
          margin: [0, 0, 0, 4],
        },
        subTitle: {
          fontSize: 10,
          color: '#8898aa',
          alignment: 'left',
        },
        dataLabel: {
          fontSize: 11,
          bold: true,
          color: '#8898aa',
          margin: [0, 0, 0, 4],
        },
        dataDescricao: {
          fontSize: 11,
          bold: true,
          color: '#32325d',
          margin: [0, 0, 0, 4],
        },
        fieldLabel: {
          fontSize: 10,
          bold: true,
          color: '#32325d',
          margin: [0, 0, 0, 0],
        },
        dataValue: {
          fontSize: 10,
          color: '#525f7f',
          lineHeight: 1.4,
        },
        itemText: {
          fontSize: 10,
          color: '#525f7f',
          margin: [0, 2, 0, 2],
        },
        totalText: {
          fontSize: 11,
          color: '#32325d',
          bold: true,
          margin: [0, 4, 0, 2],
        },
        footerText: {
          fontSize: 9,
          color: '#4B5563',
        },
        link: {
          color: '#8E4E38',
          bold: true,
          decoration: 'underline',
        },
      },
      defaultStyle: {
        font: 'Arial',
      },
    };

    pdfMake
      .createPdf(docDefinition)
      .download(`recibo_pagamento_${this.invoice.uuid}.pdf`);
  }

  toLocalDate(dateStr: string): Date {
    return /^\d{4}-\d{2}-\d{2}$/.test(dateStr)
      ? parse(dateStr, 'yyyy-MM-dd', new Date())
      : new Date(dateStr);
  }
}
